import { useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold, Italic, Strikethrough, Heading2, Heading3, List, ListOrdered,
  Quote, Link as LinkIcon, Image as ImageIcon, Undo, Redo, Code,
} from 'lucide-react';
import { MediaLibraryDialog } from './MediaLibraryDialog';

const Btn = ({ active, onClick, title, disabled, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    disabled={disabled}
    data-testid={testid}
    className={`h-8 w-8 inline-flex items-center justify-center rounded transition-colors ${
      active ? 'bg-sky-100 text-sky-700' : 'text-slate-600 hover:bg-slate-100'
    } disabled:opacity-30 disabled:cursor-not-allowed`}
  >
    {children}
  </button>
);

/**
 * TipTap-based WYSIWYG editor. Emits HTML on change.
 * Props: value (HTML string), onChange (html) => void, placeholder, minHeight (px)
 */
export const RichTextEditor = ({ value, onChange, placeholder = 'Write your article…', minHeight = 360 }) => {
  const [mediaOpen, setMediaOpen] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Image.configure({ inline: false, allowBase64: false, HTMLAttributes: { class: 'rounded-lg my-4 max-w-full' } }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: 'text-sky-600 underline' } }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
  });

  // Keep editor in sync if parent swaps in a new value (e.g. AI draft loads)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || '') !== current) {
      editor.commands.setContent(value || '', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) {
    return (
      <div
        className="border border-slate-200 rounded-md p-4 text-sm text-slate-400"
        style={{ minHeight }}
      >
        Loading editor…
      </div>
    );
  }

  const setLink = () => {
    const prev = editor.getAttributes('link').href || '';
    const url = window.prompt('Enter URL (leave blank to remove):', prev);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    const normalised = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    editor.chain().focus().extendMarkRange('link').setLink({ href: normalised, target: '_blank' }).run();
  };

  const insertImage = (url) => {
    if (!url) return;
    editor.chain().focus().setImage({ src: url }).run();
  };

  return (
    <div className="border border-slate-200 rounded-md overflow-hidden">
      <div className="flex flex-wrap items-center gap-0.5 bg-slate-50 border-b border-slate-200 px-2 py-1.5">
        <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold" testid="editor-bold"><Bold className="h-4 w-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic" testid="editor-italic"><Italic className="h-4 w-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough"><Strikethrough className="h-4 w-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} title="Inline code"><Code className="h-4 w-4" /></Btn>
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2"><Heading2 className="h-4 w-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Heading 3"><Heading3 className="h-4 w-4" /></Btn>
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list"><List className="h-4 w-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered list"><ListOrdered className="h-4 w-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Blockquote"><Quote className="h-4 w-4" /></Btn>
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <Btn onClick={setLink} active={editor.isActive('link')} title="Insert / edit link" testid="editor-link"><LinkIcon className="h-4 w-4" /></Btn>
        <Btn onClick={() => setMediaOpen(true)} title="Insert image from library" testid="editor-image"><ImageIcon className="h-4 w-4" /></Btn>
        <div className="ml-auto flex items-center gap-0.5">
          <Btn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo"><Undo className="h-4 w-4" /></Btn>
          <Btn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo"><Redo className="h-4 w-4" /></Btn>
        </div>
      </div>
      <EditorContent
        editor={editor}
        className="prose prose-slate max-w-none p-4 focus:outline-none [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[var(--editor-min-h)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-slate-400 [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0"
        style={{ '--editor-min-h': `${minHeight}px` }}
        data-testid="rich-text-editor"
      />
      <MediaLibraryDialog
        open={mediaOpen}
        onOpenChange={setMediaOpen}
        onSelect={(url) => insertImage(url)}
        title="Insert image into article"
      />
    </div>
  );
};

export default RichTextEditor;
